// ONE door for the WhatsApp CONSULT link (Founder 02/10/26, "1 כן … 5 כן"): a link that goes straight to a consultation
// WITH US (972506403800) with the inquiry details already written. Plain JS so every surface shares it — the React
// ConsultFloat (390 + 1280), the AMP article twin (app/page/amp/…), and the ad engine's sitelink
// (scripts/experts-ads/adwriter.mjs). The redirect itself is app/wa/consult/route.ts: it logs the click
// (he_consult_clicks: gclid/gbraid/wbraid + {keyword} + topic + ab_arm), opens a 'consult' ticket on a session that carries the
// click ids (→ the verified-lead conversion upload), and 302s to wa.me with the ONE composer's text (lib/inquiryMessage.ts).
// No ticket number in the customer's text (Founder 09-28 consult rule) — the worker ties the chat back by the prefill itself.
import { forbiddenWord } from '../scripts/kwlab/forbidden.mjs';

export const CONSULT_ORIGIN = 'https://experts.co.il';
export const CONSULT_PATH = '/wa/consult';
export const COMPANY_WA = '972506403800';
/** Sitelink text (≤25). Google's symbols policy refuses emoji in ad text — the 💬 lives on the site button, not the asset. */
export const CONSULT_SITELINK_TEXT = 'התייעצות בוואטסאפ';
/** Final URL suffix for the sitelink: Google appends gclid/gbraid/wbraid itself (auto-tagging); {keyword} is ValueTrack. */
export const CONSULT_KW_SUFFIX = 'kw={keyword}';
// 08/10: slides-a / slides-b = the "מצגת" pages A/B variants (A = city → our WhatsApp, B = real pros first); src is VARCHAR(12)
export const CONSULT_SOURCES = ['float', 'amp', 'sitelink', 'ad', 'other', 'slides-a', 'slides-b', 'slides-c', 'slides-d', 'assistant'];   // 11/10: C/D were filed as 'other'; 20/10: ct2 WhatsApp-style assistant

// 09/10 (slides variant B): the visitor's chosen ACTION closes the prefill with one natural Hebrew line (act=wa_in|wa_out|phones)
export const CONSULT_ACT_LINES = {
  wa_in: 'אשמח שמומחים יצרו איתי קשר כאן בוואטסאפ.',
  wa_out: 'אשמח לפרטים כדי ליצור קשר עם המומחים.',
  phones: 'אשמח לקבל את הטלפונים של המומחים.',
  // 11/10 slides D bottom bar "קבלו הצעות מהירות מכל המומחים": pros=<ids shown> names them server-side (fallback = this line)
  wa_all: 'אשמח לקבל הצעות מהירות מכל המומחים.',
  // 09/10 slides B pro card: per-pro action; {pro} = the pro's FIRST name (looked up server-side from pro=<id>)
  pro_wa: 'אשמח לשוחח בוואטסאפ עם {pro}.',
  pro_call: 'אשמח לדבר בטלפון עם {pro}.',
};
export const CONSULT_ACT_LINES_NOPRO = { pro_wa: 'אשמח לשוחח בוואטסאפ עם המומחה.', pro_call: 'אשמח לדבר בטלפון עם המומחה.' };

const clip = (s, n) => String(s ?? '').replace(/\s+/g, ' ').trim().slice(0, n);

/** /wa/consult?cat=<slug>&t=<topic slug>&quiz=<id>&city=<name>&need=<answers>&a=<article>&sid=<tracker session>&src=<surface> */
/** @param {{ cat?: string|null, topic?: string|null, quiz?: string|number|null, city?: string|null, need?: string|null, article?: string|number|null, sid?: string|null, src?: string|null, act?: string|null, pro?: string|number|null, ev?: string|null, evn?: string|number|null, absolute?: boolean }} [o] */
export function consultHref({ cat, topic, quiz, city, need, article, sid, src, act, pro, ev, evn, absolute = false } = {}) {
  const p = new URLSearchParams();
  if (cat) p.set('cat', clip(cat, 80));
  if (topic) p.set('t', clip(topic, 120));
  if (quiz) p.set('quiz', String(quiz).replace(/\D/g, '').slice(0, 9));
  if (city) p.set('city', clip(city, 60));
  if (need) p.set('need', clip(need, 200));
  if (article) p.set('a', String(article).replace(/\D/g, '').slice(0, 9));
  if (sid) p.set('sid', clip(sid, 64));
  if (act && CONSULT_ACT_LINES[act]) p.set('act', act);
  if (pro) p.set('pro', String(pro).replace(/\D/g, '').slice(0, 9));
  if (ev === '0' || ev === '1') p.set('ev', ev); // evening QA override (14/10/26)
  if (evn) p.set('evn', String(evn).replace(/\D/g, '').slice(0, 9)); // evening note variant shown at click time (15/10/26)
  if (src) p.set('src', CONSULT_SOURCES.includes(src) ? src : 'other');
  const qs = p.toString();
  return `${absolute ? CONSULT_ORIGIN : ''}${CONSULT_PATH}${qs ? '?' + qs : ''}`;
}

/** Reads every consult param (plus Google's click ids and the {keyword} ValueTrack) from a URLSearchParams. */
export function parseConsultParams(sp) {
  const g = k => { const v = sp.get(k); return v == null ? null : clip(v, 200) || null; };
  const id = k => { const v = g(k); return v && /^[A-Za-z0-9_\-]{8,100}$/.test(v) ? v : null; };
  const src = g('src');
  return {
    cat: g('cat'), topic: g('t'), quiz: (g('quiz') || '').replace(/\D/g, '') || null, city: g('city'), need: g('need'),
    article: Number((g('a') || '').replace(/\D/g, '')) || null, sid: (g('sid') || '').replace(/[^A-Za-z0-9_\-.:]/g, '').slice(0, 64) || null,
    src: src && CONSULT_SOURCES.includes(src) ? src : (id('gclid') || id('gbraid') || id('wbraid') ? 'ad' : 'other'),
    act: CONSULT_ACT_LINES[g('act') || ''] ? g('act') : null,
    pro: Number((g('pro') || '').replace(/\D/g, '').slice(0, 9)) || null,
    // 11/10 slides D: the pros shown (ids, order kept, ≤30) + the shown area when no city (a region label: Hebrew words only)
    pros: [...new Set((g('pros') || '').split(',').map(x => Number(x.replace(/\D/g, '').slice(0, 9)) || 0).filter(Boolean))].slice(0, 30),
    area: (x => /^[\u05d0-\u05ea'\-, ]{2,40}$/.test(x) ? x : null)(String(g('area') || '').trim()),
    evn: Number((g('evn') || '').replace(/\D/g, '').slice(0, 9)) || null,
    kw: g('kw') || g('keyword'), gclid: id('gclid'), gbraid: id('gbraid'), wbraid: id('wbraid'),
  };
}

// Words that are search modifiers, never part of the need ("אינסטלטור מומלץ בחולון" → need "" — the topic/category says it).
const KW_MODIFIERS = new Set(['מומלץ', 'מומלצת', 'מומלצים', 'הכי', 'טוב', 'טובה', 'זול', 'זולה', 'זולים', 'קרוב', 'ליד', 'לידי', 'באזור', 'באיזור', 'אזור',
  'דחוף', 'מהיר', 'מקצועי', 'מקצועית', 'אמין', 'מחיר', 'מחירים', 'עלות', 'כמה', 'עולה', 'המלצות', 'המלצה', 'ביקורות', 'טלפון', 'מספר', 'שירות']);
/** The searcher's {keyword} is used in the prefill ONLY when clean: Hebrew words only, 2-6 words after dropping the category's
 *  own words, modifiers and the city; no free-words, no digits/Latin, no ValueTrack leftovers. Otherwise null. */
/** @param {string|null|undefined} kw @param {{ categoryName?: string|null, cityName?: string|null }} [o] @returns {string|null} */
export function cleanKeyword(kw, { categoryName, cityName } = {}) {
  let t = clip(kw, 80);
  try { t = decodeURIComponent(t); } catch {}
  t = t.replace(/[+_]/g, ' ').replace(/["'״׳]/g, '').replace(/\s+/g, ' ').trim();
  if (!t || /[{}]/.test(t) || /[A-Za-z0-9]/.test(t) || /[^֐-׿\s\-]/.test(t)) return null;
  if (forbiddenWord(t)) return null;
  const drop = new Set(String(categoryName || '').split(/\s+/).filter(Boolean));
  const cityWords = String(cityName || '').split(/\s+/).filter(Boolean);
  let words = t.split(/\s+/).filter(w => !drop.has(w) && !KW_MODIFIERS.has(w));
  if (cityWords.length) { const s = words.join(' '); const c = cityWords.join(' '); words = s.replace(new RegExp(`(^|\\s)[בל]?${c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`), ' ').trim().split(/\s+/).filter(Boolean); }
  if (words.length < 2 || words.length > 6) return null;
  return words.join(' ');
}
